import type { AssessmentFormSchema } from '../types/dynamicAssessment';
import { DynamicAssessmentConstants } from '../types/dynamicAssessment';

export interface MaturityOptionUi {
  key: string;
  label: string;
  definition: string;
  optionValue: number;
}

export interface AssessmentSectionUi {
  title: string;
  description: string;
  commentPlaceholder?: string;
  /** Shown below the comment field (not in the placeholder). */
  commentExample?: string;
}

const DEFAULT_COMMENT_PLACEHOLDER = 'Share your reasoning…';

/** Split API/legacy placeholder text into a short placeholder + example line. */
export function splitCommentPlaceholder(raw?: string): {
  commentPlaceholder: string;
  commentExample?: string;
} {
  const text = (raw ?? '').trim();
  if (!text) {
    return { commentPlaceholder: DEFAULT_COMMENT_PLACEHOLDER };
  }

  const exampleMatch = text.match(/\bExample:\s*/i);
  if (exampleMatch && exampleMatch.index != null) {
    const before = text.slice(0, exampleMatch.index).replace(/\s+/g, ' ').trim();
    const example = text.slice(exampleMatch.index).trim();
    const placeholder =
      !before || /^(current|future) state comment$/i.test(before)
        ? DEFAULT_COMMENT_PLACEHOLDER
        : before;
    return { commentPlaceholder: placeholder, commentExample: example };
  }

  const paragraphBreak = text.indexOf('\n\n');
  if (paragraphBreak !== -1) {
    const head = text.slice(0, paragraphBreak).trim();
    const tail = text.slice(paragraphBreak + 2).trim();
    const placeholder =
      /^(current|future) state comment$/i.test(head) || !head
        ? DEFAULT_COMMENT_PLACEHOLDER
        : head;
    return {
      commentPlaceholder: placeholder,
      commentExample: tail || undefined,
    };
  }

  if (/^(current|future) state comment$/i.test(text)) {
    return { commentPlaceholder: DEFAULT_COMMENT_PLACEHOLDER };
  }

  return { commentPlaceholder: text };
}

/** Split section prompt copy into bullet lines (newlines or multiple questions). */
export function splitSectionDescription(description?: string): string[] {
  const text = (description ?? '').trim();
  if (!text) return [];

  const lines = text
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length > 1) return lines;

  if (/\?\s+/.test(text)) {
    return text
      .split(/(?<=\?)\s+/)
      .map((part) => part.trim())
      .filter(Boolean);
  }

  return [text];
}

export interface AssessmentFormUiConfig {
  maturityOptions: MaturityOptionUi[];
  maturityStateKeys: string[];
  stateDescriptions: Record<string, string>;
  currentSection: AssessmentSectionUi;
  futureSection: AssessmentSectionUi;
  businessValueTitle: string;
  businessValuePlaceholder: string;
  businessImpactTitle: string;
  businessImpactPlaceholder: string;
  templateVersionId?: number;
}

/** Legacy hardcoded scale — used when a sub-capability has no custom maturityOptionsJson. */
const DEFAULT_MATURITY_OPTIONS: MaturityOptionUi[] = [
  {
    key: 'aware',
    label: 'Aware',
    optionValue: 1,
    definition:
      'Capability is inefficient, complex, and does not readily support testing and extension. Quality is consistently deficient in most areas (inaccurate, unresponsive, and outdated).',
  },
  {
    key: 'reactive',
    label: 'Reactive',
    optionValue: 2,
    definition:
      'Capability is efficient but is moderately complex, difficult to test and maintain, and cannot be easily extended or leveraged for use in other systems. Quality is inconsistent or generally deficient in some areas.',
  },
  {
    key: 'proactive',
    label: 'Proactive',
    optionValue: 3,
    definition:
      'Capability is efficient and can be readily tested and maintained, though its complexity limits its reusability and extensibility. Quality generally meets expectations across most areas.',
  },
  {
    key: 'managed',
    label: 'Managed',
    optionValue: 4,
    definition:
      'Capability is efficient and can be readily tested, maintained, and reused, but with limited extensibility. Quality usually meets and occasionally exceeds some expectations.',
  },
  {
    key: 'optimized',
    label: 'Optimized',
    optionValue: 5,
    definition:
      'Capability is efficient, understandable, and readily supports testing and maintenance. The service is extensible and can be leveraged for use in other systems. Quality consistently exceeds all expectations (e.g. accurate, proactive).',
  },
];

const DEFAULT_CURRENT_SECTION: AssessmentSectionUi = {
  title: 'Current State',
  description:
    'Evaluate where your capability stands today. Select a rank from Aware to Optimized that best describes the current state and explains your reasoning in the text box provided.',
  commentPlaceholder: DEFAULT_COMMENT_PLACEHOLDER,
  commentExample:
    'Proactive Example: Data quality checks are partially automated, and some departments rely on manual processes.',
};

const DEFAULT_FUTURE_SECTION: AssessmentSectionUi = {
  title: 'Future State',
  description:
    'Define your aspiration for this capability. Choose a rank from Aware to Optimized that best represents your desired future state and explain your reasoning in the text box provided.',
  commentPlaceholder: DEFAULT_COMMENT_PLACEHOLDER,
  commentExample:
    'Optimized Example: Fully automated and consistent data quality checks are implemented. Data validation, and standardization are routine',
};

export const DEFAULT_ASSESSMENT_FORM_UI_CONFIG: AssessmentFormUiConfig = {
  maturityOptions: DEFAULT_MATURITY_OPTIONS,
  maturityStateKeys: DEFAULT_MATURITY_OPTIONS.map((option) => option.key),
  stateDescriptions: DEFAULT_MATURITY_OPTIONS.reduce<Record<string, string>>(
    (map, option) => {
      map[option.key] = option.definition;
      return map;
    },
    {}
  ),
  currentSection: DEFAULT_CURRENT_SECTION,
  futureSection: DEFAULT_FUTURE_SECTION,
  businessValueTitle: 'Business Value',
  businessValuePlaceholder: 'Describe the business value...',
  businessImpactTitle: 'Impact',
  businessImpactPlaceholder: 'Enter impact percentage...',
};

function normalizeOptionKey(label: string, optionValue: number): string {
  const normalized = label.trim().toLowerCase();
  return normalized || `option-${optionValue}`;
}

function findSection(schema: AssessmentFormSchema, code: string) {
  return schema.sections.find((section) => section.code === code);
}

function findField(schema: AssessmentFormSchema, sectionCode: string, fieldCode: string) {
  const section = findSection(schema, sectionCode);
  return section?.fields.find((field) => field.fieldCode === fieldCode);
}

function buildMaturityOptions(schema: AssessmentFormSchema): MaturityOptionUi[] | null {
  const ratingField =
    findField(
      schema,
      DynamicAssessmentConstants.sectionCodes.currentState,
      DynamicAssessmentConstants.fieldCodes.currentRating
    ) ||
    findField(
      schema,
      DynamicAssessmentConstants.sectionCodes.futureState,
      DynamicAssessmentConstants.fieldCodes.futureRating
    );

  const options = ratingField?.optionSet?.options;
  if (!options?.length) {
    return null;
  }

  return [...options]
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((option) => ({
      key: normalizeOptionKey(option.label, option.optionValue),
      label: option.label,
      optionValue: option.optionValue,
      definition: option.definition ?? '',
    }));
}

/**
 * Merges API form schema with legacy hardcoded defaults.
 * Any missing/null API value falls back to the existing UI copy.
 */
export function buildAssessmentFormUiConfig(
  schema?: AssessmentFormSchema | null
): AssessmentFormUiConfig {
  if (!schema?.sections?.length) {
    return DEFAULT_ASSESSMENT_FORM_UI_CONFIG;
  }

  const maturityOptions = buildMaturityOptions(schema) ?? DEFAULT_MATURITY_OPTIONS;
  const currentSectionApi = findSection(
    schema,
    DynamicAssessmentConstants.sectionCodes.currentState
  );
  const futureSectionApi = findSection(
    schema,
    DynamicAssessmentConstants.sectionCodes.futureState
  );
  const businessSectionApi = findSection(
    schema,
    DynamicAssessmentConstants.sectionCodes.businessValue
  );
  const currentDescriptionField = findField(
    schema,
    DynamicAssessmentConstants.sectionCodes.currentState,
    DynamicAssessmentConstants.fieldCodes.currentDescription
  );
  const futureDescriptionField = findField(
    schema,
    DynamicAssessmentConstants.sectionCodes.futureState,
    DynamicAssessmentConstants.fieldCodes.futureDescription
  );
  const businessValueField = findField(
    schema,
    DynamicAssessmentConstants.sectionCodes.businessValue,
    DynamicAssessmentConstants.fieldCodes.businessValue
  );
  const businessImpactField = findField(
    schema,
    DynamicAssessmentConstants.sectionCodes.businessValue,
    DynamicAssessmentConstants.fieldCodes.businessValueImpact
  );

  const stateDescriptions = maturityOptions.reduce<Record<string, string>>(
    (map, option) => {
      map[option.key] =
        option.definition ||
        DEFAULT_ASSESSMENT_FORM_UI_CONFIG.stateDescriptions[option.key] ||
        '';
      return map;
    },
    {}
  );

  return {
    maturityOptions,
    maturityStateKeys: maturityOptions.map((option) => option.key),
    stateDescriptions,
    currentSection: (() => {
      const fromApi = splitCommentPlaceholder(currentDescriptionField?.placeholder);
      return {
        title: currentSectionApi?.title || DEFAULT_CURRENT_SECTION.title,
        description:
          currentSectionApi?.description || DEFAULT_CURRENT_SECTION.description,
        commentPlaceholder: currentDescriptionField?.placeholder
          ? fromApi.commentPlaceholder
          : DEFAULT_CURRENT_SECTION.commentPlaceholder,
        commentExample: currentDescriptionField?.placeholder
          ? fromApi.commentExample
          : DEFAULT_CURRENT_SECTION.commentExample,
      };
    })(),
    futureSection: (() => {
      const fromApi = splitCommentPlaceholder(futureDescriptionField?.placeholder);
      return {
        title: futureSectionApi?.title || DEFAULT_FUTURE_SECTION.title,
        description:
          futureSectionApi?.description || DEFAULT_FUTURE_SECTION.description,
        commentPlaceholder: futureDescriptionField?.placeholder
          ? fromApi.commentPlaceholder
          : DEFAULT_FUTURE_SECTION.commentPlaceholder,
        commentExample: futureDescriptionField?.placeholder
          ? fromApi.commentExample
          : DEFAULT_FUTURE_SECTION.commentExample,
      };
    })(),
    businessValueTitle:
      businessValueField?.label || DEFAULT_ASSESSMENT_FORM_UI_CONFIG.businessValueTitle,
    businessValuePlaceholder:
      businessValueField?.placeholder ||
      DEFAULT_ASSESSMENT_FORM_UI_CONFIG.businessValuePlaceholder,
    businessImpactTitle:
      businessImpactField?.label ||
      DEFAULT_ASSESSMENT_FORM_UI_CONFIG.businessImpactTitle,
    businessImpactPlaceholder:
      businessImpactField?.placeholder ||
      DEFAULT_ASSESSMENT_FORM_UI_CONFIG.businessImpactPlaceholder,
    templateVersionId: schema.templateVersionId,
  };
}

export function ratingToStateKey(
  rating: number | null | undefined,
  config: AssessmentFormUiConfig = DEFAULT_ASSESSMENT_FORM_UI_CONFIG
): string {
  if (!rating) {
    return '';
  }

  const matched = config.maturityOptions.find(
    (option) => option.optionValue === rating
  );
  if (matched) {
    return matched.key;
  }

  return DEFAULT_ASSESSMENT_FORM_UI_CONFIG.maturityStateKeys[rating - 1] || '';
}

export function stateKeyToRating(
  stateKey: string | null | undefined,
  config: AssessmentFormUiConfig = DEFAULT_ASSESSMENT_FORM_UI_CONFIG
): number {
  if (!stateKey) {
    return 0;
  }

  const matched = config.maturityOptions.find((option) => option.key === stateKey);
  if (matched) {
    return matched.optionValue;
  }

  const legacyIndex =
    DEFAULT_ASSESSMENT_FORM_UI_CONFIG.maturityStateKeys.indexOf(stateKey);
  return legacyIndex >= 0 ? legacyIndex + 1 : 0;
}

export function getMaturityLabel(
  stateKey: string,
  config: AssessmentFormUiConfig = DEFAULT_ASSESSMENT_FORM_UI_CONFIG
): string {
  const matched = config.maturityOptions.find((option) => option.key === stateKey);
  if (matched?.label) {
    return matched.label;
  }

  return stateKey ? stateKey.charAt(0).toUpperCase() + stateKey.slice(1) : '';
}
